/**
 * 十字の先にピントを合わせ、手前と奥を円形ボケさせる後処理。
 * 合焦面は深度どおり残し、CoC に応じて Vogel 円盤でサンプルする。
 *
 * EffectComposer の clone() は depthTexture の Source を共有することがあり、
 * 書き込み先 FBO とサンプラーが同じ深度になって真っ黒になる。
 * シーン用とボケ用の RT を別々に用意して、それを避ける。
 */

import * as THREE from 'three';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';
import { OutputShader } from 'three/addons/shaders/OutputShader.js';
import {
  DOF_FOCUS_SPEED,
  DOF_LANDSCAPE_FOCUS,
  dofFalloff,
  resolveFocusDistance,
  smoothFocusDistance,
} from '../shared/depth-of-field';
import type { GraphicsQuality } from './graphics-quality';

const DOF_VERTEX = /* glsl */ `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const DOF_FRAGMENT = /* glsl */ `
#include <common>
#include <packing>

varying vec2 vUv;

uniform sampler2D tColor;
uniform sampler2D tDepth;
uniform float uFocus;
uniform float uNearStart;
uniform float uNearFalloff;
uniform float uFarStart;
uniform float uFarFalloff;
uniform float uMaxBlur;
uniform float uNearClip;
uniform float uFarClip;
uniform float uAspect;
uniform int uSamples;

float linearDepth(const in vec2 uv) {
  float depth = texture2D(tDepth, uv).x;
  return -perspectiveDepthToViewZ(depth, uNearClip, uFarClip);
}

float circleOfConfusion(const in float depth) {
  float nearC = 1.0 - smoothstep(uFocus - uNearStart - uNearFalloff, uFocus - uNearStart, depth);
  float farC = smoothstep(uFocus + uFarStart, uFocus + uFarStart + uFarFalloff, depth);
  return max(nearC, farC);
}

vec2 vogel(const in int index, const in int count, const in float radius) {
  float t = float(index) + 0.5;
  float r = radius * sqrt(t / float(count));
  float a = t * 2.399963229728653;
  return vec2(cos(a) / uAspect, sin(a)) * r;
}

void main() {
  float centerDepth = linearDepth(vUv);
  float centerCoc = circleOfConfusion(centerDepth);
  vec4 sharp = texture2D(tColor, vUv);

  if (centerCoc < 0.002 || uSamples < 1) {
    gl_FragColor = sharp;
    return;
  }

  float radius = centerCoc * uMaxBlur;
  vec4 color = sharp;
  float weight = 1.0;
  int count = uSamples;

  for (int i = 0; i < 24; i++) {
    if (i >= count) break;
    vec2 uv = vUv + vogel(i, count, radius);
    float sampleCoc = circleOfConfusion(linearDepth(uv));
    float w = 0.35 + 0.65 * smoothstep(centerCoc - 0.25, centerCoc + 0.05, sampleCoc);
    color += texture2D(tColor, uv) * w;
    weight += w;
  }

  gl_FragColor = color / weight;
  gl_FragColor.a = 1.0;
}
`;

function createColorTarget(): THREE.WebGLRenderTarget {
  return new THREE.WebGLRenderTarget(1, 1, {
    type: THREE.HalfFloatType,
    depthBuffer: false,
  });
}

export class DepthOfField {
  enabled = false;
  private focus = DOF_LANDSCAPE_FOCUS;
  private readonly dofTarget = createColorTarget();
  private readonly dofUniforms: {
    tColor: { value: THREE.Texture | null };
    tDepth: { value: THREE.Texture | null };
    uFocus: { value: number };
    uNearStart: { value: number };
    uNearFalloff: { value: number };
    uFarStart: { value: number };
    uFarFalloff: { value: number };
    uMaxBlur: { value: number };
    uNearClip: { value: number };
    uFarClip: { value: number };
    uAspect: { value: number };
    uSamples: { value: number };
  };
  private readonly dofMaterial: THREE.ShaderMaterial;
  private readonly dofQuad: FullScreenQuad;
  private readonly outputUniforms: {
    tDiffuse: { value: THREE.Texture | null };
    toneMappingExposure: { value: number };
  };
  private readonly outputMaterial: THREE.RawShaderMaterial;
  private readonly outputQuad: FullScreenQuad;
  private outputColorSpace: string | null = null;
  private outputToneMapping: THREE.ToneMapping | null = null;

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    private readonly camera: THREE.PerspectiveCamera
  ) {
    this.dofUniforms = {
      tColor: { value: null },
      tDepth: { value: null },
      uFocus: { value: DOF_LANDSCAPE_FOCUS },
      uNearStart: { value: 1 },
      uNearFalloff: { value: 2 },
      uFarStart: { value: 4 },
      uFarFalloff: { value: 24 },
      uMaxBlur: { value: 0.012 },
      uNearClip: { value: camera.near },
      uFarClip: { value: camera.far },
      uAspect: { value: camera.aspect },
      uSamples: { value: 16 },
    };
    this.dofMaterial = new THREE.ShaderMaterial({
      uniforms: this.dofUniforms,
      vertexShader: DOF_VERTEX,
      fragmentShader: DOF_FRAGMENT,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });
    this.dofQuad = new FullScreenQuad(this.dofMaterial);

    this.outputUniforms = {
      tDiffuse: { value: null },
      toneMappingExposure: { value: 1 },
    };
    this.outputMaterial = new THREE.RawShaderMaterial({
      name: OutputShader.name,
      uniforms: this.outputUniforms,
      vertexShader: OutputShader.vertexShader,
      fragmentShader: OutputShader.fragmentShader,
    });
    this.outputQuad = new FullScreenQuad(this.outputMaterial);
  }

  get focusDistance(): number {
    return this.focus;
  }

  applyQuality(quality: GraphicsQuality): void {
    this.dofUniforms.uSamples.value = quality.depthOfFieldSamples;
    this.dofUniforms.uMaxBlur.value = quality.depthOfFieldMaxBlur;
  }

  setSize(width: number, height: number, pixelRatio: number): void {
    const w = Math.max(1, Math.round(width * pixelRatio));
    const h = Math.max(1, Math.round(height * pixelRatio));
    this.dofTarget.setSize(w, h);
  }

  /** 線形カラーと深度を受け取り、ピンボケとトーンマップして画面へ出す。 */
  compose(
    color: THREE.Texture,
    depth: THREE.Texture,
    hitDistance: number | null,
    dt: number
  ): void {
    this.focus = smoothFocusDistance(
      this.focus,
      resolveFocusDistance(hitDistance),
      dt,
      DOF_FOCUS_SPEED
    );
    this.applyFocus(this.focus);

    const previousTarget = this.renderer.getRenderTarget();
    const previousAutoClear = this.renderer.autoClear;
    this.renderer.autoClear = true;

    let output = color;
    if (this.enabled) {
      this.dofUniforms.tColor.value = color;
      this.dofUniforms.tDepth.value = depth;
      this.dofUniforms.uAspect.value = this.camera.aspect;
      this.renderer.setRenderTarget(this.dofTarget);
      this.dofQuad.render(this.renderer);
      this.dofUniforms.tColor.value = null;
      this.dofUniforms.tDepth.value = null;
      output = this.dofTarget.texture;
    }

    this.syncOutputMaterial();
    this.outputUniforms.tDiffuse.value = output;
    this.outputUniforms.toneMappingExposure.value = this.renderer.toneMappingExposure;
    this.renderer.setRenderTarget(null);
    this.outputQuad.render(this.renderer);
    this.outputUniforms.tDiffuse.value = null;

    this.renderer.setRenderTarget(previousTarget);
    this.renderer.autoClear = previousAutoClear;
  }

  dispose(): void {
    this.dofTarget.dispose();
    this.dofMaterial.dispose();
    this.dofQuad.dispose();
    this.outputMaterial.dispose();
    this.outputQuad.dispose();
  }

  private applyFocus(focus: number): void {
    const band = dofFalloff(focus);
    this.dofUniforms.uFocus.value = focus;
    this.dofUniforms.uNearStart.value = band.nearStart;
    this.dofUniforms.uNearFalloff.value = band.nearFalloff;
    this.dofUniforms.uFarStart.value = band.farStart;
    this.dofUniforms.uFarFalloff.value = band.farFalloff;
    this.dofUniforms.uNearClip.value = this.camera.near;
    this.dofUniforms.uFarClip.value = this.camera.far;
    this.dofUniforms.uAspect.value = this.camera.aspect;
  }

  private syncOutputMaterial(): void {
    if (
      this.outputColorSpace === this.renderer.outputColorSpace &&
      this.outputToneMapping === this.renderer.toneMapping
    ) {
      return;
    }
    this.outputColorSpace = this.renderer.outputColorSpace;
    this.outputToneMapping = this.renderer.toneMapping;
    const defines: Record<string, string> = {};
    if (THREE.ColorManagement.getTransfer(this.renderer.outputColorSpace) === THREE.SRGBTransfer) {
      defines.SRGB_TRANSFER = '';
    }
    if (this.renderer.toneMapping === THREE.LinearToneMapping) defines.LINEAR_TONE_MAPPING = '';
    else if (this.renderer.toneMapping === THREE.ReinhardToneMapping) defines.REINHARD_TONE_MAPPING = '';
    else if (this.renderer.toneMapping === THREE.CineonToneMapping) defines.CINEON_TONE_MAPPING = '';
    else if (this.renderer.toneMapping === THREE.ACESFilmicToneMapping) {
      defines.ACES_FILMIC_TONE_MAPPING = '';
    } else if (this.renderer.toneMapping === THREE.AgXToneMapping) defines.AGX_TONE_MAPPING = '';
    else if (this.renderer.toneMapping === THREE.NeutralToneMapping) defines.NEUTRAL_TONE_MAPPING = '';
    this.outputMaterial.defines = defines;
    this.outputMaterial.needsUpdate = true;
  }
}
